import React, { useState, useMemo } from 'react';
import { X, Send, DollarSign, User, Briefcase, Star, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '../../contexts/ToastContext';

export default function SubmitCandidateModal({ candidate, jobId, onClose, onSuccess }) {
  const { addToast } = useToast();

  const [clientName, setClientName] = useState('');
  const [billRate, setBillRate]     = useState('');
  const [payRate, setPayRate]       = useState('');
  const [notes, setNotes]           = useState('');
  const [submitting, setSubmitting] = useState(false);

  const score = candidate?.linkedin_score?.score ?? null;

  const margin = useMemo(() => {
    const bill = parseFloat(billRate);
    const pay  = parseFloat(payRate);
    if (!isFinite(bill) || !isFinite(pay) || bill <= 0) return null;
    const diff = bill - pay;
    const pct  = (diff / bill) * 100;
    return { diff, pct };
  }, [billRate, payRate]);

  const isValid = clientName.trim().length > 0 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setSubmitting(true);
    try {
      const payload = {
        candidate_id: candidate?.candidate_id || candidate?.id,
        job_id: jobId,
        client_name: clientName.trim(),
        bill_rate: billRate === '' ? null : parseFloat(billRate),
        pay_rate:  payRate  === '' ? null : parseFloat(payRate),
        notes: notes.trim() || null,
      };

      await api.post('/submissions', payload);

      addToast(`Candidate submitted to ${payload.client_name}`, 'success');
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to submit candidate';
      addToast(typeof msg === 'string' ? msg : 'Failed to submit candidate', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="submit-candidate-modal-overlay"
      className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        data-testid="submit-candidate-modal"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <Send className="w-4 h-4 text-blue-600" />
            </div>
            <h2 className="text-base font-bold text-slate-900">Submit to Client</h2>
          </div>
          <button
            data-testid="submit-modal-close-btn"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Candidate summary */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 bg-slate-50/70 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900 truncate" data-testid="submit-modal-candidate-name">
                {candidate?.name || 'Unknown Candidate'}
              </p>
              {(candidate?.sourced_data?.current_title || candidate?.job_title) && (
                <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  {candidate?.sourced_data?.current_title || candidate?.job_title}
                </p>
              )}
            </div>
          </div>
          {score != null && (
            <span
              data-testid="submit-modal-score-badge"
              className={`flex-shrink-0 flex items-center gap-0.5 px-2 py-0.5 rounded-lg text-[11px] font-bold border ${
                score >= 80 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                score >= 60 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                              'bg-red-50 border-red-200 text-red-700'
              }`}
            >
              <Star className="w-2.5 h-2.5" /> {score}
            </span>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Client Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Client Name <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="submit-modal-client-name-input"
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g. Oracle Corp, Microsoft"
              required
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl
                         focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                         placeholder:text-slate-400 transition-all"
            />
          </div>

          {/* Rates row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Bill Rate</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 pointer-events-none">
                  $/hr
                </span>
                <input
                  data-testid="submit-modal-bill-rate-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={billRate}
                  onChange={(e) => setBillRate(e.target.value)}
                  placeholder="e.g. 85"
                  className="w-full pl-12 pr-3 py-2 text-sm border border-slate-200 rounded-xl
                             focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                             placeholder:text-slate-400 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Pay Rate</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 pointer-events-none">
                  $/hr
                </span>
                <input
                  data-testid="submit-modal-pay-rate-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={payRate}
                  onChange={(e) => setPayRate(e.target.value)}
                  placeholder="e.g. 65"
                  className="w-full pl-12 pr-3 py-2 text-sm border border-slate-200 rounded-xl
                             focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                             placeholder:text-slate-400 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Margin preview */}
          {margin && (
            <div
              data-testid="submit-modal-margin-display"
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border ${
                margin.diff >= 0
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}
            >
              <DollarSign className="w-3.5 h-3.5" />
              Margin: ${margin.diff.toFixed(2)}/hr ({margin.pct.toFixed(1)}%)
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Notes</label>
            <textarea
              data-testid="submit-modal-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context for this submission..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl
                         focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                         placeholder:text-slate-400 transition-all resize-none"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              data-testid="submit-modal-cancel-btn"
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="submit-modal-submit-btn"
              type="submit"
              disabled={!isValid}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white
                         bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed
                         rounded-xl transition-colors shadow-sm"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit to Client
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
